// frontend/components/FormTForm.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Paper,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
  Box,
  Divider,
  Chip,
  Card,
  CardContent,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  FormHelperText,
  Stepper,
  Step,
  StepLabel
} from '@mui/material';
import {
  Send as SendIcon,
  Upload as UploadIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  FamilyRestroom as FamilyIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Search as SearchIcon
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FormTForm = ({ employeeDetails: propEmployeeDetails, onSuccess, userRole, currentEmployeeId, companyId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [applicantType, setApplicantType] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeeDetails, setEmployeeDetails] = useState(propEmployeeDetails);
  const [formData, setFormData] = useState({
    applicant_name: '',
    applicant_address: '',
    relationship: '',
    legal_heir_document: null,
    legal_heir_id_proof: null,
    death_certificate: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeeStatus, setEmployeeStatus] = useState(null);

  // Fetch employees for HR dropdown
  useEffect(() => {
    if (userRole === 'hr') {
      fetchEmployees();
    }
  }, [userRole]);

  const fetchEmployees = async () => {
    setLoadingEmployees(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/form-t/employees/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees);
    } catch (error) {
      //console.error('Error fetching employees:', error);
      setError('Failed to fetch employees list');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchEmployeeDetailsForHR = async (empId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/form-t/employee?employee_id=${currentEmployeeId}&selected_employee_id=${empId}&role=hr`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployeeDetails(response.data);
      setEmployeeStatus({
        isDeceased: response.data.employee.isDeceased,
        name: response.data.employee.name,
        status: response.data.employee.employmentStatus,
        hasNominee: response.data.employee.hasNominee,
        nomineeName: response.data.employee.nomineeName
      });
      setApplicantType(''); // Reset applicant type when employee changes
      setFormData({
        applicant_name: '',
        applicant_address: '',
        relationship: '',
        legal_heir_document: null,
        legal_heir_id_proof: null,
        death_certificate: null
      });
      setError('');
      
      // Move to next step if employee is deceased
      if (response.data.employee.isDeceased) {
        setActiveStep(1);
      } else {
        setActiveStep(0);
      }
    } catch (error) {
      //console.error('Error fetching employee details:', error);
      setError(error.response?.data?.error || 'Failed to fetch employee details');
      setEmployeeDetails(null);
      setEmployeeStatus(null);
    }
  };

  const handleEmployeeChange = (event) => {
    const empId = event.target.value;
    setSelectedEmployeeId(empId);
    if (empId) {
      fetchEmployeeDetailsForHR(empId);
    } else {
      setEmployeeDetails(null);
      setEmployeeStatus(null);
      setActiveStep(0);
    }
  };

  const handleTypeChange = (event) => {
    const type = event.target.value;
    setApplicantType(type);
    const selectedOption = employeeDetails?.availableApplicants?.find(opt => opt.type === type);
    if (selectedOption) {
      setFormData({
        ...formData,
        applicant_name: selectedOption.defaultName || '',
        applicant_address: selectedOption.defaultAddress || '',
        relationship: selectedOption.defaultRelationship || ''
      });
    }
    setActiveStep(2);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);
  setError('');

  try {
    const token = localStorage.getItem('token');
    
    let response;
    
    // For employee (no files to upload), send as JSON
    if (userRole === 'employee') {
      const data = {
        applicant_type: applicantType || 'employee',
        applicant_name: employeeDetails.employee.name || '',
        applicant_address: employeeDetails?.employee?.address ||"",
        relationship: formData.relationship,
        gratuity_amount: employeeDetails.financials.gratuityAmount,
        notice_date: new Date().toISOString()
      };

      
      response = await axios.post(
        `${API_URL}/api/form-t/submit?employee_id=${currentEmployeeId}`,
        data,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } 
    // For HR (with file uploads), use FormData
    else {
      const submitData = new FormData();
      

      
      // Append all fields
      submitData.append('applicant_type', applicantType);
      submitData.append('applicant_name', formData.applicant_name);
      submitData.append('applicant_address', formData.applicant_address);
      submitData.append('relationship', formData.relationship);
      submitData.append('gratuity_amount', employeeDetails.financials.gratuityAmount.toString());
      submitData.append('notice_date', new Date().toISOString());
      submitData.append('selected_employee_id', selectedEmployeeId); // Make sure this is appended
      
      // Add documents based on applicant type
      if (applicantType === 'nominee' && formData.death_certificate) {
        submitData.append('death_certificate', formData.death_certificate);
      } else if (applicantType === 'legal_heir') {
        if (formData.legal_heir_document) {
          submitData.append('legal_heir_document', formData.legal_heir_document);
        }
        if (formData.legal_heir_id_proof) {
          submitData.append('legal_heir_id_proof', formData.legal_heir_id_proof);
        }
        if (formData.death_certificate) {
          submitData.append('death_certificate', formData.death_certificate);
        }
      }
      
  
      
      response = await axios.post(
        `${API_URL}/api/form-t/submit?employee_id=${currentEmployeeId}`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
    }

    if (response.data.success) {
      alert('Application submitted successfully! Awaiting approval.');
      onSuccess();
    }
  } catch (error) {
    //console.error('Error submitting application:', error);
    //console.error('Error response:', error.response?.data);
    setError(error.response?.data?.error || 'Failed to submit application');
  } finally {
    setSubmitting(false);
  }
};

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const applicantOptions = employeeDetails?.availableApplicants || [];
  const selectedOption = applicantOptions.find(opt => opt.type === applicantType);
  const isEligible = employeeDetails?.financials?.eligible;

  // Steps for HR flow
  const steps = ['Select Employee', 'Verify Status', 'Fill Application'];

  // Show error if employee is not eligible
  if (userRole === 'employee' && !isEligible && employeeDetails) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningIcon />}>
        {employeeDetails.employee?.isDeceased 
          ? 'You are deceased. Please contact HR to file claim through nominee or legal heir.'
          : 'You are not eligible for gratuity. Minimum 5 years of service required.'}
      </Alert>
    );
  }

  // HR View - Employee Selection First
  if (userRole === 'hr') {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon color="primary" />
          New Form T Application
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        {/* Stepper for HR Flow */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Employee Selection */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 1: Select Employee
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please select the deceased employee for whom you want to file Form T application.
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <FormLabel>Select Employee *</FormLabel>
                <Select
                  value={selectedEmployeeId}
                  onChange={handleEmployeeChange}
                  disabled={loadingEmployees}
                  required
                >
                  <MenuItem value="">Select an employee</MenuItem>
                  {employees.map(emp => (
                    <MenuItem key={emp.employee_id} value={emp.employee_id}>
                      <Box>
                        <Typography variant="body2">
                          {emp.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Status: {emp.employmentStatus}
                          {emp.hasNominee && ' | Has Nominee'}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {loadingEmployees && <CircularProgress size={20} sx={{ mt: 1 }} />}
                <FormHelperText>Only deceased employees are eligible for Form T claims</FormHelperText>
              </FormControl>

              <Button
                variant="contained"
                onClick={() => {
                  if (selectedEmployeeId) {
                    // Already fetched in handleEmployeeChange
                    if (!employeeStatus?.isDeceased) {
                      setError('This employee is alive. Only deceased employees can file claims.');
                    } else {
                      setActiveStep(1);
                    }
                  } else {
                    setError('Please select an employee');
                  }
                }}
                disabled={!selectedEmployeeId}
                sx={{ mt: 2 }}
              >
                Next
              </Button>
            </Box>
          )}

          {/* Step 2: Employee Status Verification */}
          {activeStep === 1 && employeeStatus && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 2: Employee Status Verification
              </Typography>
              
              <Card sx={{ mb: 3, bgcolor: employeeStatus.isDeceased ? '#ffebee' : '#e8f5e9' }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Employee Details
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Name:</strong> {employeeStatus.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Status:</strong> 
                        <Chip 
                          label={employeeStatus.status}
                          color={employeeStatus.isDeceased ? 'error' : 'success'}
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      </Typography>
                    </Grid>
                    {employeeStatus.hasNominee && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Registered Nominee:</strong> {employeeStatus.nomineeName}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>

              {employeeStatus.isDeceased ? (
                <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>
                  This employee is deceased. You can proceed with filing Form T claim as Nominee or Legal Heir.
                </Alert>
              ) : (
                <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
                  This employee is alive. Form T applications can only be filed for deceased employees through HR portal.
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button variant="outlined" onClick={handleBack}>
                  Back
                </Button>
                {employeeStatus.isDeceased && (
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep(2)}
                  >
                    Next: Choose Applicant Type
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Step 3: Applicant Type and Form */}
          {activeStep === 2 && employeeStatus?.isDeceased && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 3: Select Applicant Type
              </Typography>
              
              {/* Applicant Type Selection */}
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">Applicant Type *</FormLabel>
                <RadioGroup
                  value={applicantType}
                  onChange={handleTypeChange}
                >
                  <Grid container spacing={2}>
                    {applicantOptions.map(option => (
                      <Grid item xs={12} key={option.type}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            cursor: option.disabled ? 'not-allowed' : 'pointer',
                            opacity: option.disabled ? 0.6 : 1,
                            bgcolor: applicantType === option.type ? 'action.selected' : 'background.paper'
                          }}
                          onClick={() => !option.disabled && handleTypeChange({ target: { value: option.type } })}
                        >
                          <CardContent>
                            <FormControlLabel
                              value={option.type}
                              control={<Radio />}
                              label={
                                <Box>
                                  <Typography variant="subtitle1" fontWeight="bold">
                                    {option.label}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                  </Typography>
                                  {option.requiredDocuments && (
                                    <Box sx={{ mt: 0.5 }}>
                                      {option.requiredDocuments.map(doc => (
                                        <Chip 
                                          key={doc} 
                                          label={`Required: ${doc.replace(/_/g, ' ')}`} 
                                          size="small" 
                                          color="warning" 
                                          variant="outlined"
                                          sx={{ mr: 0.5, mt: 0.5 }}
                                        />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              }
                              disabled={option.disabled}
                            />
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              </FormControl>

              {applicantType && selectedOption && (
                <>
                  {/* Applicant Name */}
                  <TextField
                    fullWidth
                    label="Applicant Full Name"
                    name="applicant_name"
                    value={formData.applicant_name}
                    onChange={handleChange}
                    required
                    disabled={!selectedOption.requiresManualEntry && formData.applicant_name}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Applicant Address */}
                  <TextField
                    fullWidth
                    label="Applicant Address"
                    name="applicant_address"
                    value={formData.applicant_address}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    required
                    disabled={!selectedOption.requiresManualEntry && formData.applicant_address}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {/* Relationship */}
                  <TextField
                    fullWidth
                    label="Relationship to Employee"
                    name="relationship"
                    value={formData.relationship}
                    onChange={handleChange}
                    required
                    disabled={applicantType === 'employee'}
                    placeholder={applicantType === 'employee' ? 'Self' : 'e.g., Spouse, Son, Daughter'}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FamilyIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText={applicantType === 'employee' ? 'Auto-filled as "Self"' : ''}
                  />

                  {/* Common Documents */}
                  {(applicantType === 'nominee' || applicantType === 'legal_heir') && (
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<UploadIcon />}
                      sx={{ mb: 2, mr: 2 }}
                    >
                      Upload Death Certificate
                      <input
                        type="file"
                        name="death_certificate"
                        onChange={handleChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                        hidden
                        required
                      />
                    </Button>
                  )}

                  {/* Legal Heir Documents */}
                  {applicantType === 'legal_heir' && (
                    <>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                        sx={{ mb: 2, mr: 2 }}
                      >
                        Upload Legal Heir Certificate
                        <input
                          type="file"
                          name="legal_heir_document"
                          onChange={handleChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          hidden
                          required
                        />
                      </Button>
                      
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadIcon />}
                        sx={{ mb: 2 }}
                      >
                        Upload Identity Proof (Aadhar/PAN)
                        <input
                          type="file"
                          name="legal_heir_id_proof"
                          onChange={handleChange}
                          accept=".pdf,.jpg,.jpeg,.png"
                          hidden
                          required
                        />
                      </Button>
                    </>
                  )}

                  {/* Display uploaded files */}
                  {formData.death_certificate && (
                    <Chip 
                      label={`Death Certificate: ${formData.death_certificate.name}`} 
                      onDelete={() => setFormData({...formData, death_certificate: null})}
                      sx={{ mb: 1, mr: 1 }}
                    />
                  )}
                  {formData.legal_heir_document && (
                    <Chip 
                      label={`Legal Heir Certificate: ${formData.legal_heir_document.name}`} 
                      onDelete={() => setFormData({...formData, legal_heir_document: null})}
                      sx={{ mb: 1, mr: 1 }}
                    />
                  )}
                  {formData.legal_heir_id_proof && (
                    <Chip 
                      label={`Identity Proof: ${formData.legal_heir_id_proof.name}`} 
                      onDelete={() => setFormData({...formData, legal_heir_id_proof: null})}
                      sx={{ mb: 2 }}
                    />
                  )}

                  {/* Gratuity Amount Display */}
                  <TextField
                    fullWidth
                    label="Gratuity Amount"
                    value={`₹ ${employeeDetails?.financials?.gratuityAmount?.toLocaleString() || 0}`}
                    disabled
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    helperText="Amount calculated based on last salary and years of service"
                  />

                  {/* Employee Information Summary */}
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Employee:</strong> {employeeDetails.employee.name}<br/>
                      <strong>Service Tenure:</strong> {employeeDetails.employee.tenureYears} years<br/>
                      <strong>Status:</strong> {employeeDetails.employee.employmentStatus}
                      {employeeDetails.employee.hasNominee && employeeDetails.employee.isDeceased && (
                        <><br/><strong>Nominee:</strong> {employeeDetails.employee.nomineeName} ({employeeDetails.employee.nomineeRelationship})</>
                      )}
                    </Typography>
                  </Alert>

                  {/* Error Message */}
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {/* Navigation Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button variant="outlined" onClick={handleBack}>
                      Back
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting || !applicantType}
                      startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                    >
                      {submitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}
        </form>
      </Paper>
    );
  }

  // Employee View (Simplified - No Stepper)
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SendIcon color="primary" />
        New Form T Application
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSubmit}>
        {/* Applicant Type Selection - Only Self for Employee */}
        {employeeDetails && (
          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend">Applicant Type *</FormLabel>
            <RadioGroup value="employee">
              <Card variant="outlined" sx={{ bgcolor: 'action.selected' }}>
                <CardContent>
                  <FormControlLabel
                    value="employee"
                    control={<Radio checked={true} />}
                    label={
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Self (Employee)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Apply for gratuity as the employee
                        </Typography>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            </RadioGroup>
          </FormControl>
        )}

        {/* Applicant Name - Auto-filled */}
        <TextField
          fullWidth
          label="Applicant Full Name"
          name="applicant_name"
          value={employeeDetails?.employee?.name || ''}
          disabled
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Applicant Address - Auto-filled */}
        <TextField
          fullWidth
          label="Applicant Address"
          name="applicant_address"
          value={employeeDetails?.employee?.address || ''}
          multiline
          rows={3}
          disabled
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <HomeIcon color="action" />
              </InputAdornment>
            ),
          }}
        />

        {/* Relationship - Auto-filled as Self */}
        <TextField
          fullWidth
          label="Relationship to Employee"
          value="Self"
          disabled
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FamilyIcon color="action" />
              </InputAdornment>
            ),
          }}
          helperText="Auto-filled as 'Self'"
        />

        {/* Gratuity Amount Display */}
        <TextField
          fullWidth
          label="Gratuity Amount"
          value={`₹ ${employeeDetails?.financials?.gratuityAmount?.toLocaleString() || 0}`}
          disabled
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MoneyIcon color="action" />
              </InputAdornment>
            ),
          }}
          helperText="Amount calculated based on last salary and years of service"
        />

        {/* Employee Information Summary */}
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Employee:</strong> {employeeDetails?.employee?.name}<br/>
            <strong>Service Tenure:</strong> {employeeDetails?.employee?.tenureYears} years<br/>
            <strong>Status:</strong> {employeeDetails?.employee?.employmentStatus}
          </Typography>
        </Alert>

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
          sx={{ mt: 2 }}
        >
          {submitting ? 'Submitting...' : 'Submit Application'}
        </Button>
      </form>
    </Paper>
  );
};

export default FormTForm;